import React, { Fragment, useState, useEffect, useReducer, useRef } from 'react'
import { string } from 'prop-types'
import styled from 'styled-components'
import ContentWrapper from '../../shared/ContentWrapper'
import Loader from '../../shared/Loader'
import PageDescription from '../../shared/PageDescription'
import SearchableTable from '../../shared/SearchableTable'
import Button from '../../shared/Button'
import Tooltip from '../../shared/Tooltip2'
import { navigate } from '@reach/router'
import {
  RED,
  TEXT_SMALL,
  METAL,
  ICON_SIZE_SMALL,
  ICON_SIZE_MEDIUM,
  MANTIS,
  YELLOW,
  APP_STATE,
} from '../../../constants'
import { SECONDARY_BUTTON } from '../../../constants/buttonThemes'
import {
  fetchAuditList,
  constructServiceBusMessage,
  saveNewRulesetToDatabase,
  getRegionDataForDialogUI,
  getRulesetsForDialogUI,
  getRulesForDialogUI,
  getPartnersForDialogUI,
  startNewAudit,
  resetRulesetCreationState,
  auditFileReset,
  uploadFeedAuditFile,
  getRulesForAuditAmendmentDialogUI,
  removeRulesFromAudit,
  resetAuditRuleAmendmentState,
} from './actions'
import { initialState, reducer } from './reducer'
import AuditRequestDialog from '../../shared/FeedAuditorComponents/AuditRequestDialog'
import RulesetCreationDialog from '../../shared/FeedAuditorComponents/RulesetCreationDialog'
import AuditFilteringDialog from '../../shared/FeedAuditorComponents/AuditFilteringDialog'
import FeedAuditorTopButtons from '../../shared/FeedAuditorComponents/FeedAuditorTopButtons'
import AuditRuleAmendmentDialog from '../../shared/FeedAuditorComponents/AuditRuleAmendmentDialog'
import { userShape } from '../../../types'
import { FaClock, FaCheckCircle, FaMinusCircle, FaEdit } from 'react-icons/fa'
import { auditUrl } from '../../../constants/urls'
import useDebounce from '../../../hooks/UseDebounce'

const Spacer = styled.div`
  padding-top: 10px;
`

const RowRadius = styled.div`
  .ReactTable .rt-tbody .rt-tr-group {
    border-radius: 5px;
  }
`
const CellCenter = styled.div`
  vertical-align: middle;
  display: table-cell;
  white-space: no-wrap;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`
const OverflowWrapper = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`

const StatusInline = styled.span`
  display: flex;
`

const ButtonsInRowContainer = styled.div`
  display: flex;
  gap: 10px;
  height: 100%;
`

const FeedAuditList = ({ t, organisationId, user }) => {
  const { isAdmin, id } = user
  const [state, dispatch] = useReducer(reducer, initialState)
  const {
    auditList,
    auditListState,
    pageTotal,
    page,
    auditModalRegionsState,
    auditModalRulesState,
    auditModalPartnersState,
    auditModalRulesetsState,
    auditModalRegionsData,
    auditModalRulesData,
    auditModalPartnersData,
    auditModalRulesetsData,
    rulesetCreationState,
    rulesetCreationResponse,
    auditFileState,
    auditModalAmendmentRulesState,
    auditModalAmendmentRulesData,
    auditRuleAmendmentState,
    auditRuleAmendmentResponse,
  } = state
  const [
    isAuditRequestDialogVisible,
    setIsAuditRequestDialogVisible,
  ] = useState(false)
  const [
    isRulesetCreationDialogVisible,
    setIsRulesetCreationDialogVisible,
  ] = useState(false)
  const [isFilteringVisible, setIsFilteringVisible] = useState(false)
  const [showNullOrgIds, setShowNullOrgIds] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const debouncedGetAudits = useDebounce(() => {
    getAudits(false)
  }, 500)
  const [sorting, setSorting] = useState({ id: 'timestamp', desc: true })
  const [filterQuery, setFilterQuery] = useState({
    fromDate: '',
    toDate: '',
    scoreFrom: 0,
    scoreTo: 100,
    successFilter: false,
    failFilter: false,
    progressFilter: false,
    recalculatingFilter: false,
    recalculationFailedFilter: false,
  })
  const [auditToAmend, setAuditToAmend] = useState('')
  const [
    isAuditRuleAmendmentDialogVisible,
    setIsAuditRuleAmendmentDialogVisible,
  ] = useState(false)
  const [rulesToRemove, setRulesToRemove] = useState([])

  const prevOrgValue = useRef(organisationId)

  useEffect(
    () => {
      // When ruleset is successfully created (DONE) close dialog and reset state
      if (rulesetCreationState == APP_STATE.DONE) {
        setIsRulesetCreationDialogVisible(false)
        resetRulesetCreationState()
        getRulesetsForDialogUI(dispatch)
      }
    },
    [rulesetCreationState]
  )

  useEffect(
    () => {
      // When ruleset is successfully created (DONE) close dialog and reset state
      if (auditRuleAmendmentState == APP_STATE.DONE) {
        setIsAuditRuleAmendmentDialogVisible(false)
        resetAuditRuleAmendmentState()
        getAudits(true)
      }
    },
    [auditRuleAmendmentState]
  )

  useEffect(() => {
    getRegionDataForDialogUI(dispatch)
    getRulesForDialogUI(dispatch)
    getPartnersForDialogUI(dispatch)
    getRulesetsForDialogUI(dispatch)
  }, [])

  useEffect(
    () => {
      if (prevOrgValue.current !== organisationId) {
        prevOrgValue.current = organisationId
        fetchAuditList(
          true,
          showNullOrgIds ? null : organisationId,
          dispatch,
          null,
          t,
          '',
          { id: 'timestamp', desc: true },
          {
            fromDate: '',
            toDate: '',
            scoreFrom: 0,
            scoreTo: 100,
            successFilter: false,
            failFilter: false,
            progressFilter: false,
            recalculatingFilter: false,
            recalculationFailedFilter: false,
          }
        )
        resetSortSearchFilterValues()
      } else {
        getAudits(false)
      }
    },
    [filterQuery, sorting, organisationId]
  )

  useEffect(
    () => {
      debouncedGetAudits(searchValue)
    },
    [searchValue]
  )

  const updateSearchValue = searchValue => {
    if (searchValue.length > 100) {
      searchValue = searchValue.substring(0, 100)
    }
    setSearchValue(searchValue)
  }

  const resetSortSearchFilterValues = () => {
    setSorting({ id: 'timestamp', desc: true })
    setSearchValue('')
    setFilterQuery({
      fromDate: '',
      toDate: '',
      scoreFrom: 0,
      scoreTo: 100,
      successFilter: false,
      failFilter: false,
      progressFilter: false,
      recalculatingFilter: false,
      recalculationFailedFilter: false,
    })
  }

  const toggleShowNullAudits = () => {
    resetSortSearchFilterValues()
    setShowNullOrgIds(!showNullOrgIds)
    fetchAuditList(
      false,
      !showNullOrgIds ? null : organisationId,
      dispatch,
      null,
      t,
      '',
      { id: 'timestamp', desc: true },
      {
        fromDate: '',
        toDate: '',
        scoreFrom: 0,
        scoreTo: 100,
        successFilter: false,
        failFilter: false,
        progressFilter: false,
        recalculatingFilter: false,
        recalculationFailedFilter: false,
      }
    )
  }

  const onAuditRequestSaveClicked = (
    label,
    regionISOCode,
    fieldDelimiter,
    rulesetId,
    rules,
    partners,
    feedFilePath,
    companyName,
    brandName,
    orgId,
    brandId,
    internalAuditFlag,
    collectProductFailureDetails
  ) => {
    const auditRequest = constructServiceBusMessage(
      label,
      regionISOCode,
      fieldDelimiter,
      rulesetId,
      rules,
      partners,
      feedFilePath,
      companyName,
      brandName,
      collectProductFailureDetails
    )

    startNewAudit(
      t,
      auditRequest,
      orgId,
      showNullOrgIds ? null : organisationId,
      internalAuditFlag,
      dispatch,
      page,
      searchValue,
      sorting,
      filterQuery,
      companyName,
      label
    )

    dispatch(auditFileReset())
    setIsAuditRequestDialogVisible(false)
  }

  const uploadAuditFile = data => {
    uploadFeedAuditFile(data, dispatch, t)
  }

  function getAudits(reload) {
    fetchAuditList(
      reload,
      showNullOrgIds ? null : organisationId,
      dispatch,
      page,
      t,
      searchValue,
      sorting,
      filterQuery
    )
  }

  const onRulesetCreationSaveClicked = (newRulesetName, selectedRulesList) => {
    // Ruleset creation dialog is closed in useEffect above
    saveNewRulesetToDatabase(dispatch, newRulesetName, selectedRulesList, t)
  }

  const onRuleAmendmentSaveClicked = selectedRulesList => {
    removeRulesFromAudit(dispatch, auditToAmend, selectedRulesList, t)
  }

  const openInNewTab = row => {
    let url = `${auditUrl}/report_${row.id}`
    window.open(url, '_blank', 'noreferrer')
  }
  const editRulesForAudit = row => {
    setAuditToAmend(row.id)
    getRulesForAuditAmendmentDialogUI(row.id, dispatch)
    setIsAuditRuleAmendmentDialogVisible(true)
  }
  const TableColumns = t => {
    let columns = [
      {
        Header: t('feed_audit_table_header2'),
        accessor: 'timestamp',
        sortingFn: 'datetime',
        Cell: row => {
          const {
            original: { timestamp },
          } = row
          return timestamp ? (
            <OverflowWrapper>
              {t('dateValueInput', {
                value: new Date(timestamp),
              })}
            </OverflowWrapper>
          ) : (
            ''
          )
        },
        minWidth: 100,
        maxWidth: 220,
      },
      {
        Header: t('feed_audit_table_header1'),
        accessor: 'label',

        Cell: row => {
          const {
            original: { label },
          } = row
          return <OverflowWrapper> {label} </OverflowWrapper>
        },
        minWidth: 200,
      },
      {
        Header: t('feed_audit_table_header7'),
        accessor: 'brandName',

        Cell: row => {
          const {
            original: { brandName },
          } = row
          return <OverflowWrapper> {brandName} </OverflowWrapper>
        },
        minWidth: 100,
      },
      {
        Header: t('feed_audit_table_header5'),
        accessor: 'score',
        sortUndefined: 'last',
        Cell: row => {
          if (row.original.score != null) {
            const content = <>{row.original.score + '%'}</>
            return <OverflowWrapper>{content}</OverflowWrapper>
          }
        },
        minWidth: 50,
        maxWidth: 80,
      },
      {
        Header: t('feed_audit_table_header4'),
        accessor: 'progressStatus',
        Cell: row => {
          const content = (
            <>
              {auditStatus[row.original.progressStatus]
                ? AuditStatusIcon(row.original.progressStatus)
                : ' '}
              {TextNoWrap(t(row.original.progressStatus))}
            </>
          )
          if (
            row.original.humanReadableErrorMessage &&
            row.original.humanReadableErrorMessage.length > 0
          ) {
            const titleContent = row.original.humanReadableErrorMessage.join(
              '\r\n'
            )
            return <StatusInline title={titleContent}>{content}</StatusInline>
          } else {
            return <>{content}</>
          }
        },
        minWidth: 90,
        maxWidth: 150,
      },
      {
        Header: t('view_reports'),
        width: 220,
        Cell: row => {
          const {
            original: { id },
          } = row
          return (
            <ButtonsInRowContainer>
              <Button
                onClick={() => openInNewTab(row.original)}
                theme={SECONDARY_BUTTON}
                fontSize={TEXT_SMALL}
                disabled={
                  row.original.progressStatus !== 1 &&
                  row.original.progressStatus !== 3 &&
                  row.original.progressStatus !== 4
                }
              >
                {t('feedcheck')}
              </Button>
              <Button
                onClick={() =>
                  navigate(
                    `/${organisationId}/admin/product-failure-details/${
                      row.original.id
                    }`,
                    { state: { auditLabel: row.original.label } }
                  )
                }
                theme={SECONDARY_BUTTON}
                fontSize={TEXT_SMALL}
                disabled={
                  (row.original.progressStatus !== 1 &&
                    row.original.progressStatus !== 3 &&
                    row.original.progressStatus !== 4) ||
                  !row.original.productFailureDetailsPresent
                }
              >
                {t('failures')}
              </Button>
              <Button
                onClick={() => editRulesForAudit(row.original)}
                theme={SECONDARY_BUTTON}
                fontSize={TEXT_SMALL}
                disabled={
                  row.original.progressStatus !== 1 &&
                  row.original.progressStatus !== 4
                }
                title={t('feed_audit_edit_rules')}
              >
                <FaEdit size={ICON_SIZE_SMALL} />
              </Button>
            </ButtonsInRowContainer>
          )
        },
      },
    ]

    // Only show the 'client' column if admin and viewing audits for non-feed manager clients
    if (isAdmin && showNullOrgIds) {
      let indexToReplaceBrandName = 2

      columns[indexToReplaceBrandName] = {
        Header: t('feed_audit_table_header3'),
        accessor: 'companyName',
        Cell: row => {
          const {
            original: { companyName },
          } = row
          return <OverflowWrapper> {companyName} </OverflowWrapper>
        },
        width: 130,
        maxWidth: 200,
      }
    }
    return columns
  }

  const topButtonsContent = [
    // Buttons for everyone
    {
      name: t('new_audit'),
      onClick: () => {
        setIsAuditRequestDialogVisible(true)
      },
    },
    ...(isAdmin
      ? [
          // Buttons for admins only
          {
            name: t('create_ruleset_title'),
            onClick: () => {
              setIsRulesetCreationDialogVisible(true)
            },
          },
        ]
      : []),
    {
      name: t('reload_data'),
      onClick: () =>
        fetchAuditList(
          true,
          showNullOrgIds ? null : organisationId,
          dispatch,
          page,
          t,
          searchValue,
          sorting,
          filterQuery
        ),
    },
  ]
  function AuditStatusIcon(status) {
    var auditStatusInformation = auditStatus[status]
    var color = auditStatusInformation.color
    return (
      <div
        style={{
          marginRight: '5px',
          verticalAlign: 'middle',
          display: 'inline-block',
          position: 'relative',
          height: `${ICON_SIZE_MEDIUM}px`,
        }}
      >
        <auditStatusInformation.icon
          size={ICON_SIZE_MEDIUM}
          verticalAlign="middle"
          display="inline-block"
          color={color}
        />
      </div>
    )
  }
  function TextNoWrap(text) {
    return (
      <CellCenter>
        <span
          fontSize={TEXT_SMALL}
          font-family="DM Sans"
          color={METAL}
          text-overflow="ellipsis"
        >
          {text == 0
            ? t('audit_failed')
            : text == 1
            ? t('audit_success')
            : text == 2
            ? t('audit_progress')
            : text == 3
            ? t('audit_recalculating')
            : text == 4
            ? t('audit_recalculationFailed')
            : text == 5
            ? t('audit_pending')
            : ''}
        </span>
      </CellCenter>
    )
  }
  const auditStatus = {
    0: { color: RED, icon: FaMinusCircle },
    1: { color: MANTIS, icon: FaCheckCircle },
    2: { color: YELLOW, icon: FaClock },
    3: { color: YELLOW, icon: FaClock },
    4: { color: RED, icon: FaCheckCircle },
    5: { color: YELLOW, icon: FaClock },
  }
  const cancelRulesetCreationDialog = () => {
    setIsRulesetCreationDialogVisible(false)
    dispatch(resetRulesetCreationState())
  }

  const cancelRuleAmendmentDialog = () => {
    setIsAuditRuleAmendmentDialogVisible(false)
    dispatch(resetAuditRuleAmendmentState())
  }

  if (!auditListState || auditListState === 'FETCHING') {
    return <Loader />
  }
  return (
    <Fragment>
      <AuditRequestDialog
        onCancel={() => {
          setIsAuditRequestDialogVisible(false)
          dispatch(auditFileReset())
        }}
        onSave={onAuditRequestSaveClicked}
        onFileUpload={uploadAuditFile}
        visible={isAuditRequestDialogVisible}
        isAdmin={isAdmin}
        regionList={auditModalRegionsData}
        rulesetList={auditModalRulesetsData}
        rulesList={auditModalRulesData}
        partnersList={auditModalPartnersData}
        auditModalRegionsState={auditModalRegionsState}
        auditModalRulesState={auditModalRulesState}
        auditModalPartnersState={auditModalPartnersState}
        auditModalRulesetsState={auditModalRulesetsState}
        auditFileState={auditFileState}
      />
      <RulesetCreationDialog
        onCancel={cancelRulesetCreationDialog}
        onSave={onRulesetCreationSaveClicked}
        visible={isRulesetCreationDialogVisible}
        rulesetList={auditModalRulesetsData}
        rulesList={auditModalRulesData}
        auditModalRulesState={auditModalRulesState}
        auditModalRulesetsState={auditModalRulesetsState}
        rulesetCreationState={rulesetCreationState}
        rulesetCreationResponse={rulesetCreationResponse}
      />
      <AuditFilteringDialog
        onClose={() => setIsFilteringVisible(false)}
        visible={isFilteringVisible}
        onSave={() => setIsFilteringVisible(false)}
        filterQuery={filterQuery}
        setFilterQuery={setFilterQuery}
      />
      <AuditRuleAmendmentDialog
        onClose={cancelRuleAmendmentDialog}
        onSave={onRuleAmendmentSaveClicked}
        visible={isAuditRuleAmendmentDialogVisible}
        auditToAmend={auditToAmend}
        setRulesToRemove={setRulesToRemove}
        auditModalAmendmentRulesState={auditModalAmendmentRulesState}
        rulesList={auditModalAmendmentRulesData}
        auditRuleAmendmentState={auditRuleAmendmentState}
        auditRuleAmendmentResponse={auditRuleAmendmentResponse}
      />
      <PageDescription
        heading={t('feed_audit')}
        description={t('feed_audit_description')}
      />
      <FeedAuditorTopButtons
        buttons={topButtonsContent}
        checkboxText={t('feed_audit_null_org_id')}
        isAdmin={isAdmin}
        showNullOrgIds={showNullOrgIds}
        setShowNullOrgIds={toggleShowNullAudits}
      />
      <Spacer />
      <ContentWrapper>
        <RowRadius>
          <SearchableTable
            columns={TableColumns(t, organisationId, dispatch, page)}
            data={auditList}
            isUsersTable
            page={page}
            pageSize={10}
            pageTotal={pageTotal}
            selectNextPage={() =>
              fetchAuditList(
                false,
                showNullOrgIds ? null : organisationId,
                dispatch,
                page + 1,
                t,
                searchValue,
                sorting,
                filterQuery
              )
            }
            selectPreviousPage={() =>
              fetchAuditList(
                false,
                showNullOrgIds ? null : organisationId,
                dispatch,
                page - 1,
                t,
                searchValue,
                sorting,
                filterQuery
              )
            }
            showPagination={false}
            sortable={false}
            searchValue={searchValue}
            onSearchChange={e => updateSearchValue(e.target.value)}
            sortingState={sorting}
            handleSort={e => setSorting(e[0])}
            filtering={isFilteringVisible}
            setFiltering={() => setIsFilteringVisible(true)}
            filterQuery={filterQuery}
          />
        </RowRadius>
      </ContentWrapper>
    </Fragment>
  )
}

FeedAuditList.propTypes = {
  organisationId: string.isRequired,
  user: userShape,
}

export default FeedAuditList
